import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { channelAPI, userAPI, meetingAPI } from '../utils/api';
import Sidebar from '../components/Sidebar';
import ChatView from '../components/ChatView';
import VideoCallView from '../components/VideoCallView';
import MeetingRoomView from '../components/MeetingRoomView';
import JoinMeetingPage from './JoinMeetingPage';
import MessageSearch from '../components/MessageSearch';
import ProfileSettings from '../components/ProfileSettings';
import StatusView from '../components/StatusView';
import IncomingCallModal from '../components/IncomingCallModal';
import OutgoingCallScreen from '../components/OutgoingCallScreen';
import { useCallManager } from '../hooks/useCallManager';
import AdminDashboard from './AdminDashboard';
import AIVideoGenerator from '../components/AIVideoGenerator';
import VideoGallery from '../components/VideoGallery';
import Leaderboard from '../components/Leaderboard';
import WalletView from '../components/WalletView';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LogOut, Plus, Users, MessageSquare, Search, Settings, Shield, Wifi, WifiOff, Phone, UserPlus, Share2, Video, Sparkles, CalendarPlus, Link2, Copy, Clock, Calendar, ExternalLink, ChevronDown, ChevronUp, Check, Wallet } from 'lucide-react';
import { showNotification, subscribeToPush, requestNotificationPermission } from '../utils/notifications';
import { unlockAudio, playMessageSound, vibrateMessage, isAppVisible } from '../utils/soundManager';
import { track } from '../utils/firebase';
import { isNativePlatform, initNativePush, setupNativePushListeners } from '../utils/nativePush';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';

const Dashboard = () => {
  const { user, logout, socket, connectionState } = useAuth();
  const {
    callState, incomingCallData, outgoingCallData,
    acceptCall, rejectCall, endCall: endCallHook, startCall,
  } = useCallManager();
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  const [showAddContactDialog, setShowAddContactDialog] = useState(false);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAIVideo, setShowAIVideo] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [aiVideoInitial, setAiVideoInitial] = useState({ prompt: '', template: null });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [addContactPhone, setAddContactPhone] = useState('');
  const [addContactError, setAddContactError] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [notOnApp, setNotOnApp] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [showJoinMeeting, setShowJoinMeeting] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [meetingType, setMeetingType] = useState('instant');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [createdMeetingLink, setCreatedMeetingLink] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [showUpcomingMeetings, setShowUpcomingMeetings] = useState(false);
  const selectedChannelRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = user?.role === 'admin';

  // Keep ref in sync so socket handler can read latest value
  useEffect(() => { selectedChannelRef.current = selectedChannel; }, [selectedChannel]);

  useEffect(() => {
    loadChannels();
    loadUsers();
    // Use native push on Capacitor, web push on browser
    if (isNativePlatform()) {
      initNativePush();
    } else {
      subscribeToPush();
    }
    requestNotificationPermission();
    unlockAudio();
    if (user && !user.phone) {
      setShowPhonePrompt(true);
    }
    // Auto-sync contacts on app start (WhatsApp-style)
    autoSyncContacts();
    // Push initial state so Android back button doesn't exit app immediately
    if (!window.history.state?.view) {
      window.history.replaceState({ view: 'home' }, '');
    }
  }, []);

  // ── Native push listeners (Capacitor only) ──
  useEffect(() => {
    if (!isNativePlatform()) return;
    const cleanup = setupNativePushListeners(
      // onCallReceived — native notification tapped for a call
      (data) => {
        toast(`Incoming call from ${data.caller_name || 'Someone'}`, { duration: 30000 });
      },
      // onMessageReceived — native notification for a message
      (data) => {
        if (data.title) toast(data.title, { description: data.body, duration: 5000 });
        loadChannels();
      }
    );
    return cleanup;
  }, []);


  // ── Auto contact sync on login/app open ──
  const autoSyncContacts = async () => {
    try {
      // Check if Contact Picker API is available AND we haven't synced recently
      const lastSync = localStorage.getItem('callchat_last_contact_sync');
      const now = Date.now();
      // Only auto-prompt once per session (or every 24h)
      if (lastSync && now - parseInt(lastSync, 10) < 86400000) {
        // Already synced within 24h — just refresh user list
        return;
      }

      if ('contacts' in navigator && 'ContactsManager' in window) {
        // Auto-trigger Contact Picker (requires user gesture on some browsers)
        // On Android Chrome PWA, this works on app open
        try {
          const contacts = await navigator.contacts.select(['tel'], { multiple: true });
          const phones = contacts
            .flatMap(c => c.tel || [])
            .map(t => t.value || t)
            .filter(Boolean);
          if (phones.length > 0) {
            const response = await userAPI.syncContacts(phones);
            const synced = response.data.total_synced || 0;
            if (synced > 0) {
              toast.success(`Found ${synced} contacts on CallChat`);
            }
            localStorage.setItem('callchat_last_contact_sync', String(now));
            loadUsers();
            loadChannels();
          }
        } catch (e) {
          // User cancelled picker or not supported in context — silent fail
          if (e.name !== 'TypeError' && e.name !== 'InvalidStateError') {
            console.warn('[ContactSync] Auto-sync picker error:', e);
          }
        }
      }
      // If Contact Picker not available, just load existing matched contacts (no prompt)
    } catch (e) {
      console.warn('[ContactSync] Auto-sync error:', e);
    }
  };

  // Manual contact sync: Contact Picker or fallback
  const handleSyncContacts = async () => {
    setSyncing(true);
    try {
      if ('contacts' in navigator && 'ContactsManager' in window) {
        const contacts = await navigator.contacts.select(['tel'], { multiple: true });
        const phones = contacts
          .flatMap(c => c.tel || [])
          .map(t => t.value || t)
          .filter(Boolean);
        if (phones.length > 0) {
          const response = await userAPI.syncContacts(phones);
          setNotOnApp(response.data.not_on_app || []);
          toast.success(`Synced! Found ${response.data.total_synced} contacts on CallChat`);
          localStorage.setItem('callchat_last_contact_sync', String(Date.now()));
          loadUsers();
          loadChannels();
        } else {
          toast.info('No phone numbers found in selected contacts');
        }
      } else {
        toast.info('Contact Picker not supported. Use "Add by phone" below.');
        setShowAddContactDialog(true);
      }
    } catch (error) {
      if (error.name !== 'TypeError') {
        toast.error('Contact sync failed');
      }
      setShowAddContactDialog(true);
    } finally {
      setSyncing(false);
    }
  };

  const handleAddContact = async () => {
    if (!addContactPhone.trim()) {
      setAddContactError('Enter a phone number');
      return;
    }
    setAddContactError('');
    try {
      const response = await userAPI.addContact(addContactPhone.trim());
      toast.success(`Added ${response.data.user.name} to contacts!`);
      track.addContact();
      setAddContactPhone('');
      setAddContactError('');
      loadUsers();
      loadChannels();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Contact not found on CallChat';
      setAddContactError(msg);
    }
  };

  const handleUpdatePhone = async () => {
    if (!phoneInput.trim()) return;
    try {
      await userAPI.updatePhone(phoneInput.trim());
      toast.success('Phone number saved!');
      setShowPhonePrompt(false);
      setPhoneInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone');
    }
  };

  const handleInvite = (phone) => {
    const text = encodeURIComponent(`Hey! Join me on CallChat - the best messaging app! Download at callchat.online`);
    if (navigator.share) {
      navigator.share({ title: 'Join CallChat', text: decodeURIComponent(text), url: 'https://callchat.online' });
    } else {
      window.open(`sms:${phone}?body=${text}`, '_blank');
    }
  };

  // ── Socket: message notifications only (calls handled by useCallManager) ──
  useEffect(() => {
    if (socket) {
      const handleUpdate = (data) => {
        if (data.type === 'new_message') {
          loadChannels();
          const isFromMe = data.data.sender_id === user.id;
          const isInActiveChat = selectedChannelRef.current && data.data.channel_id === selectedChannelRef.current.id;

          if (!isFromMe) {
            playMessageSound();
            vibrateMessage();

            if (!isInActiveChat) {
              const senderName = data.data.sender_name || 'New message';
              const preview = data.data.content || 'Sent a message';
              toast(senderName, { description: preview.slice(0, 80), duration: 4000 });
            }

            if (!isAppVisible()) {
              showNotification('New Message', {
                body: data.data.content || 'You have a new message',
                tag: `msg-${data.data.channel_id}`,
                data: { type: 'message', channelId: data.data.channel_id },
              });
            }
          }
        }
      };

      socket.on('update', handleUpdate);
      return () => socket.off('update', handleUpdate);
    }
  }, [socket, user]);

  const loadChannels = async () => {
    try {
      const response = await channelAPI.getChannels();
      setChannels(response.data);
    } catch (error) {
      console.error('Failed to load channels:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const createDirectChat = async (userId) => {
    try {
      const response = await channelAPI.createChannel({
        channel_type: 'direct',
        participant_ids: [userId],
      });
      setChannels([...channels, response.data]);
      handleSelectChannel(response.data);
      setShowNewChatDialog(false);
      navigate('/');
    } catch (error) {
      toast.error('Failed to create chat');
    }
  };

  const createGroup = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Select at least one user');
      return;
    }
    try {
      const response = await channelAPI.createChannel({
        name: groupName || 'New Group',
        channel_type: 'group',
        participant_ids: selectedUsers,
      });
      setChannels([...channels, response.data]);
      handleSelectChannel(response.data);
      setShowNewGroupDialog(false);
      setSelectedUsers([]);
      setGroupName('');
      navigate('/');
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelectChannel = (channel) => {
    setSelectedChannel(channel);
    setShowAdmin(false);
    if (window.innerWidth < 768) {
      setShowSidebar(false);
      window.history.pushState({ view: 'chat', channelId: channel?.id }, '');
    }
  };

  const handleBackToSidebar = () => {
    setShowSidebar(true);
    setSelectedChannel(null);
  };

  // Handle Android/browser back button to navigate within app instead of exiting
  useEffect(() => {
    const handlePopState = (e) => {
      // Don't interfere with navigation when on a call
      if (window.location.pathname.startsWith('/call/')) return;

      // Close full-screen overlay views (AI Video, Gallery, Leaderboard, Wallet, Settings, Search, Admin)
      if (showAIVideo) { setShowAIVideo(false); setAiVideoInitial({ prompt: '', template: null }); window.history.pushState(null, ''); return; }
      if (showGallery) { setShowGallery(false); window.history.pushState(null, ''); return; }
      if (showLeaderboard) { setShowLeaderboard(false); window.history.pushState(null, ''); return; }
      if (showSettings) { setShowSettings(false); window.history.pushState(null, ''); return; }
      if (showSearch) { setShowSearch(false); window.history.pushState(null, ''); return; }
      if (showAdmin) { setShowAdmin(false); window.history.pushState(null, ''); return; }
      if (showWallet) { setShowWallet(false); window.history.pushState(null, ''); return; }

      // If we're in a chat view on mobile, go back to sidebar
      if (selectedChannel && window.innerWidth < 768) {
        setShowSidebar(true);
        setSelectedChannel(null);
        window.history.pushState(null, '');
        return;
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedChannel, showSettings, showSearch, showAdmin, showWallet, showAIVideo, showGallery, showLeaderboard]);

  const handleSearchSelect = (channel, message) => {
    handleSelectChannel(channel);
    navigate('/');
    setTimeout(() => {
      const messageElement = document.querySelector(`[data-testid="message-${message.id}"]`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        messageElement.classList.add('highlight-flash');
        setTimeout(() => messageElement.classList.remove('highlight-flash'), 2000);
      }
    }, 500);
  };

  // ── Meeting functions ──
  const fetchUpcomingMeetings = async () => {
    try {
      const res = await meetingAPI.getMeetings();
      const meetings = (res.data || []).filter(m => m.status !== 'ended');
      setUpcomingMeetings(meetings);
    } catch (e) {}
  };

  useEffect(() => { fetchUpcomingMeetings(); }, []);

  const handleCreateMeeting = async () => {
    if (!meetingTitle.trim()) { toast.error('Enter a meeting title'); return; }
    if (meetingType === 'scheduled' && (!scheduledDate || !scheduledTime)) {
      toast.error('Pick a date and time for the scheduled meeting'); return;
    }
    setCreatingMeeting(true);
    try {
      const payload = {
        title: meetingTitle.trim(),
        meeting_type: meetingType,
        max_participants: 50,
      };
      if (meetingType === 'scheduled') {
        payload.scheduled_at = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }
      const res = await meetingAPI.createMeeting(payload);
      if (meetingType === 'instant') {
        setActiveMeeting(res.data);
        setShowCreateMeeting(false);
      } else {
        // Show the link instead of joining immediately
        const joinUrl = `${window.location.origin}/join/${res.data.join_code}`;
        setCreatedMeetingLink({ ...res.data, joinUrl });
      }
      setMeetingTitle('');
      setScheduledDate('');
      setScheduledTime('');
      setMeetingType('instant');
      fetchUpcomingMeetings();
      toast.success(meetingType === 'instant' ? 'Meeting created!' : 'Meeting scheduled!');
    } catch (e) {
      toast.error('Failed to create meeting');
    } finally {
      setCreatingMeeting(false);
    }
  };

  const handleJoinByCode = async () => {
    let code = joinCodeInput.trim().toLowerCase();
    if (!code) { toast.error('Enter a meeting code'); return; }
    // Extract join code from full URL (e.g., https://callchat.online/join/abc-defg)
    const urlMatch = code.match(/\/join\/([a-z0-9-]+)/);
    if (urlMatch) code = urlMatch[1];
    // Strip any remaining URL parts - keep only the code pattern
    code = code.replace(/^https?:\/\/.*\//, '').trim();
    try {
      const res = await meetingAPI.joinMeeting(code);
      setActiveMeeting(res.data);
      setShowJoinMeeting(false);
      setJoinCodeInput('');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Meeting not found or has ended';
      toast.error(detail);
    }
  };

  // ── Active Meeting Room ──
  if (activeMeeting) {
    return (
      <MeetingRoomView
        meeting={activeMeeting}
        socket={socket}
        onLeave={() => setActiveMeeting(null)}
      />
    );
  }

  // Show Admin Dashboard
  if (showAdmin) {
    return (
      <div className="flex h-screen bg-[#F8FAFC]">
        {/* Global Call Overlays — always render on top */}
        {callState === 'incoming' && incomingCallData && (
          <IncomingCallModal callData={incomingCallData} onAccept={acceptCall} onReject={rejectCall} />
        )}
        {callState === 'outgoing' && outgoingCallData && (
          <OutgoingCallScreen callData={outgoingCallData} onCancel={() => endCallHook(outgoingCallData.channel_id)} />
        )}
        <AdminDashboard onBack={() => setShowAdmin(false)} />
      </div>
    );
  }

  // Show AI Video Generator
  if (showAIVideo) {
    return (
      <div className="flex h-screen bg-[#F8FAFC]">
        {callState === 'incoming' && incomingCallData && (
          <IncomingCallModal callData={incomingCallData} onAccept={acceptCall} onReject={rejectCall} />
        )}
        {callState === 'outgoing' && outgoingCallData && (
          <OutgoingCallScreen callData={outgoingCallData} onCancel={() => endCallHook(outgoingCallData.channel_id)} />
        )}
        <AIVideoGenerator
          onBack={() => { setShowAIVideo(false); setAiVideoInitial({ prompt: '', template: null }); }}
          onOpenGallery={() => { setShowAIVideo(false); setShowGallery(true); window.history.pushState({ view: 'gallery' }, ''); }}
          onOpenLeaderboard={() => { setShowAIVideo(false); setShowLeaderboard(true); window.history.pushState({ view: 'leaderboard' }, ''); }}
          initialPrompt={aiVideoInitial.prompt}
          initialTemplate={aiVideoInitial.template}
        />
      </div>
    );
  }

  // Show Video Gallery
  if (showGallery) {
    track.viewGallery();
    return (
      <div className="flex h-screen bg-[#0F172A]">
        {callState === 'incoming' && incomingCallData && (
          <IncomingCallModal callData={incomingCallData} onAccept={acceptCall} onReject={rejectCall} />
        )}
        {callState === 'outgoing' && outgoingCallData && (
          <OutgoingCallScreen callData={outgoingCallData} onCancel={() => endCallHook(outgoingCallData.channel_id)} />
        )}
        <VideoGallery
          onBack={() => setShowGallery(false)}
          onGenerateSimilar={(prompt, template) => {
            setAiVideoInitial({ prompt, template });
            setShowGallery(false);
            setShowAIVideo(true);
            window.history.pushState({ view: 'aivideo' }, '');
          }}
        />
      </div>
    );
  }

  // Show Leaderboard
  if (showLeaderboard) {
    track.viewLeaderboard();
    return (
      <div className="flex h-screen bg-[#F8FAFC]">
        {callState === 'incoming' && incomingCallData && (
          <IncomingCallModal callData={incomingCallData} onAccept={acceptCall} onReject={rejectCall} />
        )}
        {callState === 'outgoing' && outgoingCallData && (
          <OutgoingCallScreen callData={outgoingCallData} onCancel={() => endCallHook(outgoingCallData.channel_id)} />
        )}
        <Leaderboard
          onBack={() => setShowLeaderboard(false)}
          onOpenGallery={() => { setShowLeaderboard(false); setShowGallery(true); window.history.pushState({ view: 'gallery' }, ''); }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen h-dvh-safe bg-[#F8FAFC] overflow-hidden">
      {/* Incoming Call Modal (managed by useCallManager) */}
      {callState === 'incoming' && incomingCallData && (
        <IncomingCallModal
          callData={incomingCallData}
          onAccept={acceptCall}
          onReject={rejectCall}
        />
      )}
      {/* Outgoing Call Screen (managed by useCallManager) */}
      {callState === 'outgoing' && outgoingCallData && (
        <OutgoingCallScreen
          callData={outgoingCallData}
          onCancel={() => endCallHook(outgoingCallData.channel_id)}
        />
      )}
      {/* Global Connection Status Banner */}
      {connectionState !== 'connected' && (
        <div
          className={`px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2 shrink-0 ${
            connectionState === 'reconnecting' ? 'bg-orange-500 text-white' :
            connectionState === 'connecting' ? 'bg-blue-500 text-white' :
            'bg-red-500 text-white'
          }`}
          data-testid="global-connection-banner"
        >
          <WifiOff className="w-4 h-4" />
          {connectionState === 'reconnecting' && 'Socket Reconnecting...'}
          {connectionState === 'connecting' && 'Socket Connecting...'}
          {connectionState === 'disconnected' && 'Socket Disconnected'}
        </div>
      )}
      <div className="flex flex-1 min-h-0">
      {/* Sidebar */}
      <div className={`${showSidebar ? 'flex' : 'hidden'} md:flex w-full md:w-80 bg-[#F1F5F9] border-r border-[#E2E8F0] flex-col`}>
        <div className="glass-header px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-lg font-bold text-[#111B21]">CallChat</h1>
            <div className="flex items-center gap-0.5">
              <div className="flex items-center mr-1" data-testid="socket-status-indicator">
                {connectionState === 'connected' ? (
                  <Wifi className="w-3.5 h-3.5 text-[#00A884]" />
                ) : connectionState === 'reconnecting' ? (
                  <WifiOff className="w-3.5 h-3.5 text-orange-500 animate-pulse" />
                ) : (
                  <WifiOff className="w-3.5 h-3.5 text-red-500" />
                )}
              </div>
              {isAdmin && (
                <button onClick={() => { setShowAdmin(true); window.history.pushState({ view: 'admin' }, ''); }} className="p-2 rounded-full hover:bg-white/50 transition-colors" data-testid="admin-button" title="Admin">
                  <Shield className="w-[18px] h-[18px] text-amber-500" />
                </button>
              )}
              <button onClick={() => { setShowAIVideo(true); window.history.pushState({ view: 'aivideo' }, ''); }} className="p-2 rounded-full hover:bg-white/50 transition-colors" data-testid="ai-video-button" title="AI Video">
                <Video className="w-[18px] h-[18px] text-[#7C3AED]" />
              </button>
              <button onClick={() => { setShowWallet(true); window.history.pushState({ view: 'wallet' }, ''); }} className="p-2 rounded-full hover:bg-white/50 transition-colors" data-testid="wallet-button" title="Wallet">
                <Wallet className="w-[18px] h-[18px] text-[#10b981]" />
              </button>
              <button onClick={() => { setShowSearch(true); window.history.pushState({ view: 'search' }, ''); }} className="p-2 rounded-full hover:bg-white/50 transition-colors" data-testid="search-button">
                <Search className="w-[18px] h-[18px] text-[#667781]" />
              </button>
              <button onClick={() => { setShowSettings(true); window.history.pushState({ view: 'settings' }, ''); }} className="p-2 rounded-full hover:bg-white/50 transition-colors" data-testid="settings-button">
                <Settings className="w-[18px] h-[18px] text-[#667781]" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2.5 cursor-pointer hover:bg-white/40 rounded-xl p-2 -mx-1 transition-colors" onClick={() => { setShowSettings(true); window.history.pushState({ view: 'settings' }, ''); }}>
            <Avatar className="h-10 w-10" data-testid="user-avatar">
              <AvatarImage src={userAPI.getAvatarUrl(user?.avatar_url)} />
              <AvatarFallback className="bg-[#4338CA] text-white text-sm">{user?.name?.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[14px] text-[#111B21] truncate" data-testid="user-name">{user?.name}</p>
              <p className="text-[12px] text-[#667781] truncate">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Status/Stories */}
        <div className="border-b border-[#E2E8F0]">
          <StatusView />
        </div>

        <div className="p-3 space-y-2">
          {/* FAB button for new actions */}
          <button
            onClick={() => setShowNewChatDialog(true)}
            className="w-full py-2.5 rounded-xl bg-[#4338CA] hover:bg-[#3730A3] text-white font-semibold text-[14px] flex items-center justify-center gap-2 shadow-md transition-all active:scale-[0.98]"
            data-testid="new-chat-button"
          >
            <Plus className="w-4 h-4" /> New
          </button>

          {/* Bottom Sheet for New Chat */}
          {showNewChatDialog === true && (
            <>
              <div className="bottom-sheet-overlay" onClick={() => setShowNewChatDialog(false)} />
              <div className="bottom-sheet" data-testid="new-chat-sheet">
                <div className="bottom-sheet-handle" />
                <div className="px-4 pb-2">
                  <h3 className="text-[16px] font-semibold text-[#111B21] mb-3">New</h3>
                  <div className="space-y-0.5">
                    <button
                      onClick={() => setShowNewChatDialog('contacts')}
                      className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-[#F0F2F5] transition-colors"
                      data-testid="sheet-new-chat"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#4338CA] flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-[14px] font-medium text-[#111B21]">New Chat</p>
                        <p className="text-[12px] text-[#667781]">Start a conversation</p>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowNewChatDialog(false); setShowNewGroupDialog(true); }}
                      className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-[#F0F2F5] transition-colors"
                      data-testid="sheet-new-group"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#00A884] flex items-center justify-center">
                        <Users className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-[14px] font-medium text-[#111B21]">New Group</p>
                        <p className="text-[12px] text-[#667781]">Create a group chat</p>
                      </div>
                    </button>
                    <button
                      onClick={() => { setShowNewChatDialog(false); setShowJoinMeeting(true); }}
                      className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-[#F0F2F5] transition-colors"
                      data-testid="sheet-join-meeting"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#7C3AED] flex items-center justify-center">
                        <Video className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-[14px] font-medium text-[#111B21]">Join Meeting</p>
                        <p className="text-[12px] text-[#667781]">Enter a meeting code</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Contact picker dialog (opened from bottom sheet) */}
          <Dialog open={showNewChatDialog === 'contacts'} onOpenChange={(open) => { if (!open) setShowNewChatDialog(false); }}>
            <DialogContent data-testid="new-chat-dialog">
              <DialogHeader>
                <DialogTitle className="text-[16px]">Select Contact</DialogTitle>
              </DialogHeader>
              <div className="flex gap-2 mb-2">
                <Button variant="outline" size="sm" onClick={handleSyncContacts} disabled={syncing} className="flex-1 gap-1 h-9 text-[13px] rounded-lg" data-testid="sync-contacts-button">
                  <Phone className="w-3.5 h-3.5" /> {syncing ? 'Syncing...' : 'Sync'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setShowNewChatDialog(false); setShowAddContactDialog(true); }} className="flex-1 gap-1 h-9 text-[13px] rounded-lg" data-testid="open-add-contact-button">
                  <UserPlus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              <ScrollArea className="h-[350px]">
                <div className="space-y-0.5">
                  {users.length === 0 && (
                    <div className="text-center py-8 text-[#8696A0]">
                      <Phone className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-[13px] font-medium">No contacts yet</p>
                      <p className="text-[12px] mt-1">Sync or add contacts to start chatting</p>
                    </div>
                  )}
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center gap-3 p-2.5 hover:bg-[#F0F2F5] rounded-xl cursor-pointer transition-colors" onClick={() => createDirectChat(u.id)} data-testid={`user-item-${u.id}`}>
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-[#4338CA] text-white text-sm">{u.name.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-medium text-[#111B21]">{u.name}</p>
                        <p className="text-[12px] text-[#667781] truncate">{u.phone || u.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>

          {/* Group Creation Dialog — with search + chips */}
          <Dialog open={showNewGroupDialog} onOpenChange={setShowNewGroupDialog}>
            <DialogContent data-testid="new-group-dialog">
              <DialogHeader>
                <DialogTitle className="text-[16px]">New Group</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input
                  placeholder="Group name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="h-10 rounded-xl text-[14px]"
                  data-testid="group-name-input"
                />
                {/* Selected user chips */}
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="selected-chips">
                    {selectedUsers.map((uid) => {
                      const u = users.find(x => x.id === uid);
                      return u ? (
                        <span key={uid} className="user-chip">
                          <span>{u.name.split(' ')[0]}</span>
                          <button onClick={() => setSelectedUsers(selectedUsers.filter(id => id !== uid))} data-testid={`remove-chip-${uid}`}>&times;</button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                {/* Search input */}
                <Input
                  placeholder="Search contacts..."
                  className="h-9 rounded-xl text-[13px]"
                  data-testid="group-search-input"
                  onChange={(e) => setGroupSearchQuery && setGroupSearchQuery(e.target.value)}
                />
                <ScrollArea className="h-[250px]">
                  <div className="space-y-0.5">
                    {users.length === 0 && <p className="text-center text-[13px] text-[#8696A0] py-4">No contacts. Add contacts first.</p>}
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center gap-3 p-2 hover:bg-[#F0F2F5] rounded-xl cursor-pointer transition-colors" onClick={() => {
                        if (selectedUsers.includes(u.id)) {
                          setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                        } else {
                          setSelectedUsers([...selectedUsers, u.id]);
                        }
                      }} data-testid={`user-checkbox-${u.id}`}>
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${selectedUsers.includes(u.id) ? 'bg-[#4338CA] border-[#4338CA]' : 'border-[#CBD5E1]'}`}>
                          {selectedUsers.includes(u.id) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-[#4338CA] text-white text-sm">{u.name.charAt(0).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className="text-[14px] text-[#111B21]">{u.name}</span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <Button onClick={createGroup} className="w-full h-11 rounded-xl bg-[#4338CA] hover:bg-[#3730A3] text-[14px] font-semibold" disabled={!groupName.trim() || selectedUsers.length < 2} data-testid="create-group-button">
                  Create Group ({selectedUsers.length} selected)
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Generate AI Video - Big CTA Button */}
          <button
            onClick={() => { setShowAIVideo(true); window.history.pushState({ view: 'aivideo' }, ''); }}
            className="w-full mt-3 py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#4338CA] hover:from-[#6D28D9] hover:to-[#3730A3] text-white font-semibold text-base flex items-center justify-center gap-2.5 shadow-lg shadow-purple-500/20 transition-all active:scale-[0.98]"
            data-testid="generate-ai-video-cta"
          >
            <Sparkles className="w-5 h-5" />
            Generate AI Video
          </button>

          {/* Meeting Buttons */}
          <div className="flex gap-2 mt-2">
            <Dialog open={showCreateMeeting} onOpenChange={(open) => { setShowCreateMeeting(open); if (!open) { setCreatedMeetingLink(null); setMeetingType('instant'); setScheduledDate(''); setScheduledTime(''); } }}>
              <DialogTrigger asChild>
                <button className="flex-1 py-2.5 px-3 rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]" data-testid="create-meeting-btn">
                  <CalendarPlus className="w-4 h-4" /> New Meeting
                </button>
              </DialogTrigger>
              <DialogContent data-testid="create-meeting-dialog">
                <DialogHeader>
                  <DialogTitle>{createdMeetingLink ? 'Meeting Scheduled!' : 'Create Meeting Room'}</DialogTitle>
                </DialogHeader>

                {createdMeetingLink ? (
                  <div className="space-y-4">
                    <div className="bg-[#F0FDF4] border border-green-200 rounded-xl p-4 space-y-3">
                      <p className="font-semibold text-[#0F172A]">{createdMeetingLink.title}</p>
                      <div className="flex items-center gap-2 text-sm text-[#475569]">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(createdMeetingLink.scheduled_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#475569]">
                        <Clock className="w-4 h-4" />
                        <span>{new Date(createdMeetingLink.scheduled_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div>
                      <Label>Share this link with participants</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input value={createdMeetingLink.joinUrl} readOnly className="text-sm font-mono" data-testid="scheduled-meeting-link" />
                        <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(createdMeetingLink.joinUrl); toast.success('Link copied!'); }} data-testid="copy-scheduled-link">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-[#94A3B8] mt-1">Code: <span className="font-mono font-semibold">{createdMeetingLink.join_code}</span></p>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => { setActiveMeeting(createdMeetingLink); setShowCreateMeeting(false); setCreatedMeetingLink(null); }} className="flex-1 bg-[#059669] hover:bg-[#047857]" data-testid="join-scheduled-now-btn">
                        Join Now
                      </Button>
                      <Button variant="outline" onClick={() => { setShowCreateMeeting(false); setCreatedMeetingLink(null); }} className="flex-1" data-testid="close-scheduled-btn">
                        Close
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="meetingTitle">Meeting Title</Label>
                      <Input
                        id="meetingTitle"
                        placeholder="e.g. Math Class, Team Standup"
                        value={meetingTitle}
                        onChange={(e) => setMeetingTitle(e.target.value)}
                        data-testid="meeting-title-input"
                      />
                    </div>

                    {/* Meeting Type Toggle */}
                    <div>
                      <Label>Meeting Type</Label>
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => setMeetingType('instant')}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-all border ${meetingType === 'instant' ? 'bg-[#059669] text-white border-[#059669]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:bg-[#F8FAFC]'}`}
                          data-testid="meeting-type-instant"
                        >
                          <Video className="w-4 h-4" /> Start Now
                        </button>
                        <button
                          onClick={() => setMeetingType('scheduled')}
                          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-all border ${meetingType === 'scheduled' ? 'bg-[#4338CA] text-white border-[#4338CA]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:bg-[#F8FAFC]'}`}
                          data-testid="meeting-type-scheduled"
                        >
                          <Calendar className="w-4 h-4" /> Schedule
                        </button>
                      </div>
                    </div>

                    {/* Date/Time pickers for scheduled */}
                    {meetingType === 'scheduled' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="scheduledDate">Date</Label>
                          <Input
                            id="scheduledDate"
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            data-testid="meeting-date-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="scheduledTime">Time</Label>
                          <Input
                            id="scheduledTime"
                            type="time"
                            value={scheduledTime}
                            onChange={(e) => setScheduledTime(e.target.value)}
                            data-testid="meeting-time-input"
                          />
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleCreateMeeting}
                      disabled={creatingMeeting}
                      className={`w-full ${meetingType === 'scheduled' ? 'bg-[#4338CA] hover:bg-[#3730A3]' : 'bg-[#059669] hover:bg-[#047857]'}`}
                      data-testid="confirm-create-meeting-btn"
                    >
                      {creatingMeeting ? 'Creating...' : meetingType === 'instant' ? 'Create & Join' : 'Schedule & Get Link'}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={showJoinMeeting} onOpenChange={setShowJoinMeeting}>
              <DialogTrigger asChild>
                <button className="flex-1 py-2.5 px-3 rounded-xl bg-[#1E293B] hover:bg-[#334155] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]" data-testid="join-meeting-btn">
                  <Link2 className="w-4 h-4" /> Join Meeting
                </button>
              </DialogTrigger>
              <DialogContent data-testid="join-meeting-dialog">
                <DialogHeader>
                  <DialogTitle>Join a Meeting</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="joinCode">Meeting Code</Label>
                    <Input
                      id="joinCode"
                      placeholder="e.g. abc-defg or paste meeting link"
                      value={joinCodeInput}
                      onChange={(e) => setJoinCodeInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleJoinByCode()}
                      data-testid="join-code-input"
                    />
                    <p className="text-xs text-[#94A3B8] mt-1">Enter the code or paste the meeting link</p>
                  </div>
                  <Button
                    onClick={handleJoinByCode}
                    className="w-full bg-[#4338CA] hover:bg-[#3730A3]"
                    data-testid="confirm-join-code-btn"
                  >
                    Join
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Upcoming Meetings - Collapsible Button */}
          {upcomingMeetings.length > 0 && (
            <div className="mt-2" data-testid="upcoming-meetings">
              <button
                onClick={() => setShowUpcomingMeetings(!showUpcomingMeetings)}
                className="w-full py-2.5 px-3 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#0F172A] font-semibold text-sm flex items-center justify-between transition-all"
                data-testid="toggle-upcoming-meetings"
              >
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#4338CA]" />
                  My Meetings ({upcomingMeetings.length})
                </span>
                {showUpcomingMeetings ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
              </button>
              {showUpcomingMeetings && (
                <div className="space-y-1.5 mt-1.5 max-h-[250px] overflow-y-auto">
                  {upcomingMeetings.map((m) => (
                    <div key={m.id} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2.5 space-y-1.5" data-testid={`upcoming-meeting-${m.id}`}>
                      <p className="text-sm font-semibold text-[#0F172A] truncate">{m.title}</p>
                      {m.scheduled_at && (
                        <div className="flex items-center gap-3 text-xs text-[#64748B]">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(m.scheduled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(m.scheduled_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setActiveMeeting(m)}
                          className="flex-1 text-xs py-1.5 rounded-md bg-[#059669] hover:bg-[#047857] text-white font-medium transition-colors"
                          data-testid={`join-upcoming-${m.id}`}
                        >Join</button>
                        <button
                          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join/${m.join_code}`); toast.success('Link copied!'); }}
                          className="px-2 py-1.5 rounded-md bg-white border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors"
                          title="Copy link"
                          data-testid={`copy-upcoming-${m.id}`}
                        ><Copy className="w-3 h-3 text-[#64748B]" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Download APK Button - hide in installed app (WebView/PWA/standalone) */}
          {!(window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || /wv|WebView/i.test(navigator.userAgent)) && (
          <a
            href="/CallChat.apk"
            download="CallChat.apk"
            className="w-full mt-2 py-3 px-4 rounded-xl bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] no-underline"
            data-testid="download-apk-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Install App (Faster & Better)
          </a>
          )}
        </div>

        {/* Add Contact Dialog */}
        <Dialog open={showAddContactDialog} onOpenChange={(open) => { setShowAddContactDialog(open); if (!open) setAddContactError(''); }}>
          <DialogContent data-testid="add-contact-dialog" className="top-[35%] sm:top-[50%]">
            <DialogHeader>
              <DialogTitle>Add Contact</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Phone Number</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="tel"
                    placeholder="+919876543210"
                    value={addContactPhone}
                    onChange={(e) => { setAddContactPhone(e.target.value); setAddContactError(''); }}
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleAddContact()}
                    data-testid="add-contact-phone-input"
                  />
                  <Button onClick={handleAddContact} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="add-contact-submit-button">
                    Add
                  </Button>
                </div>
                {addContactError ? (
                  <p className="text-xs text-red-500 mt-1" data-testid="add-contact-error">{addContactError}</p>
                ) : (
                  <p className="text-xs text-[#94A3B8] mt-1">Enter phone number of a CallChat user</p>
                )}
              </div>
              {notOnApp.length > 0 && (
                <div>
                  <Label className="text-[#94A3B8]">Not on CallChat</Label>
                  <div className="space-y-2 mt-2">
                    {notOnApp.map((phone) => (
                      <div key={phone} className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded-md">
                        <span className="text-sm text-[#475569]">{phone}</span>
                        <Button size="sm" variant="ghost" onClick={() => handleInvite(phone)} className="gap-1 text-[#4338CA]" data-testid={`invite-${phone}`}>
                          <Share2 className="w-3 h-3" /> Invite
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Phone Prompt for existing users */}
        <Dialog open={showPhonePrompt} onOpenChange={setShowPhonePrompt}>
          <DialogContent data-testid="phone-prompt-dialog">
            <DialogHeader>
              <DialogTitle>Add Your Phone Number</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#475569]">
                CallChat now uses phone numbers for contact discovery (like WhatsApp).
                Add your number so friends can find you.
              </p>
              <div>
                <Label>Phone Number</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="tel"
                    placeholder="+919876543210"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    data-testid="phone-prompt-input"
                  />
                  <Button onClick={handleUpdatePhone} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="phone-prompt-submit">
                    Save
                  </Button>
                </div>
              </div>
              <Button variant="ghost" onClick={() => setShowPhonePrompt(false)} className="w-full text-[#94A3B8]" data-testid="phone-prompt-skip">
                Skip for now
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <ScrollArea className="flex-1">
          <Sidebar
            channels={channels}
            selectedChannel={selectedChannel}
            onSelectChannel={handleSelectChannel}
            users={users}
            currentUser={user}
          />
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className={`${!showSidebar ? 'flex' : 'hidden'} md:flex flex-1 flex-col`}>
        <Routes>
          <Route
            path="/"
            element={
              selectedChannel ? (
                <ChatView channel={selectedChannel} socket={socket} onBack={handleBackToSidebar} onStartCall={startCall} />
              ) : (
                <div className="flex-1 flex items-center justify-center text-[#94A3B8]">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Select a chat to start messaging</p>
                  </div>
                </div>
              )
            }
          />
        </Routes>
      </div>

      {/* VideoCallView rendered outside layout wrapper so it's always visible (fixed position z-50) */}
      <Routes>
        <Route path="/call/:channelId" element={<VideoCallView socket={socket} onEndCall={endCallHook} />} />
      </Routes>

      {/* Search Full-screen */}
      {showSearch && (
        <MessageSearch
          open={showSearch}
          onClose={() => setShowSearch(false)}
          onSelectMessage={handleSearchSelect}
        />
      )}

      {/* Profile Settings Full-screen */}
      {showSettings && (
        <ProfileSettings
          open={showSettings}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Wallet Full-screen */}
      <WalletView
        open={showWallet}
        onClose={() => setShowWallet(false)}
        token={localStorage.getItem('token')}
      />
      </div>
    </div>
  );
};

export default Dashboard;
